import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

declare const sha512: (message: string) => string;

export type SavedCardApiEnv = 'QA' | 'PRD';

const SAVED_CARD_API_URLS: Record<SavedCardApiEnv, string> = {
  QA: 'https://qas.api.epago.pe',
  PRD: 'https://api.epago.pe',
};
export interface SavedCardAuthHeaders {
  identifier: string;
  signedElement: string;
  signature: string;
  transactionId: string;
}

export interface CardRegisterCreateRequest {
  order: {
    multipleCustomerIndicator: boolean;
    currency: { code: string };
    number: string;
    type: string;
    cardRegister: boolean;
    integrationMode: number;
    country: { code: string };
    customer: {
      identifier: string;
      name: string;
      lastName: string;
      phone: string;
      email: string;
      document: { type: string; number: string };
      address: {
        country: string;
        levels: string[];
        line1: string;
        zip: string;
      };
    };
    metadata: unknown[];
  };
  settings: {
    expiration: { type: string; date: string };
    language: string;
  };
}

export interface CardRegisterCreateResponse {
  success: boolean;
  message: { code: string; text: string };
  data: {
    order: {
      identifier: string;
      link: string;
      number: string;
      [key: string]: unknown;
    };
    settings: unknown;
  };
}

export interface CustomerCard {
  paymentMethodToken: string;
  token: string;
  bin: string;
  last4: string;
  masked: string;
  brand: string;
  expiration: string;
  cardName: string;
  merchantCode: string;
  alias: string;
  defaultCard: boolean;
  tokenSource: string;
  alternativePaymentMethods: unknown;
}

export interface CustomerCardsResponse {
  success: boolean;
  message: { code: string; text: string };
  data: CustomerCard[];
}

export interface AuthorizeSavedCardRequest {
  order: {
    number: string;
    amount: number;
    discounts: number;
    taxes: number;
    fee: number;
    shippingAmount: number;
    country: { code: string };
    currency: { code: string };
    products: Array<{
      name: string;
      quantity: number;
      unitAmount: number;
      amount: number;
      sku: string;
      category: string;
      discount: number;
      shipping: number;
    }>;
    customer: {
      identifier: string;
      name: string;
      lastName: string;
      email: string;
      phone: string;
      address: {
        country: string;
        levels: string[];
        line1: string;
        zip: string;
      };
      document: { type: string; number: string };
    };
    mode: string;
  };
  payment: {
    token: { identifier: string };
    installment: { installment: string; deferredInstallment: string };
    paymentMethod: string;
    clientRequest: {
      browserParameters: {
        javaEnabled: boolean;
        screenHeight: number;
        screenWidth: number;
        timeZoneOffset: number;
        language: string;
        colorDepth: number;
        deviceType: string;
      };
    };
  };
  settings: {
    expiration: null;
    language: string;
  };
}

export interface AuthorizeSavedCardResponse {
  success: boolean;
  message: { code: string; text: string };
  data: {
    code: string;
    message: string;
    processor?: {
      order?: { uniqueIdentifier?: string; number?: string };
      payment?: {
        authentication?: {
          purchaseValidationUrl?: string;
          scripts?: string[];
        };
      };
      result?: { code?: string; message?: string; accepted?: boolean };
    };
    [key: string]: unknown;
  };
}

@Injectable({ providedIn: 'root' })
export class SavedCardService {
  constructor(private readonly http: HttpClient) {}

  apiBaseUrl(env: SavedCardApiEnv): string {
    return SAVED_CARD_API_URLS[env];
  }
  buildAuthHeaders(
    apiKey: string,
    secret: string,
    signedElement: string,
  ): SavedCardAuthHeaders {
    const signature = sha512(`${apiKey}${signedElement}${secret}`);
    return {
      identifier: apiKey,
      signedElement,
      signature,
      transactionId: apiKey,
    };
  }

  createTimestamp(): string {
    return Date.now().toString();
  }

  createOrderNumber(): string {
    return `ORD${Date.now()}`;
  }

  createCardRegisterOrder(
    body: CardRegisterCreateRequest,
    apiKey: string,
    secret: string,
    orderNumber: string,
    timestamp: string,
    apiEnv: SavedCardApiEnv,
  ): Observable<CardRegisterCreateResponse> {
    const signedElement = `${orderNumber}${timestamp}`;
    const headers = this.buildHttpHeaders(
      this.buildAuthHeaders(apiKey, secret, signedElement),
    );
    const url = `${this.apiBaseUrl(apiEnv)}/orders/create`;
    return this.http.post<CardRegisterCreateResponse>(url, body, { headers });
  }

  getCustomerCards(
    externalCustomerReferenceId: string,
    body: { documentType: string; documentNumber: string; paymentMethod: string },
    apiKey: string,
    secret: string,
    timestamp: string,
    apiEnv: SavedCardApiEnv,
  ): Observable<CustomerCardsResponse> {
    const signedElement = timestamp;
    const headers = this.buildHttpHeaders(
      this.buildAuthHeaders(apiKey, secret, signedElement),
    );
    const url = `${this.apiBaseUrl(apiEnv)}/customer/operation/customers/${encodeURIComponent(externalCustomerReferenceId)}/cards`;
    return this.http.post<CustomerCardsResponse>(url, body, { headers });
  }

  authorizeSavedCard(
    body: AuthorizeSavedCardRequest,
    apiKey: string,
    secret: string,
    orderNumber: string,
    currencyCode: string,
    amount: number,
    apiEnv: SavedCardApiEnv,
  ): Observable<AuthorizeSavedCardResponse> {
    const signedElement = `${orderNumber}${currencyCode}${amount}`;
    const headers = this.buildHttpHeaders(
      this.buildAuthHeaders(apiKey, secret, signedElement),
    );
    const url = `${this.apiBaseUrl(apiEnv)}/payment/engine/orders/authorize/saved-card`;
    return this.http.post<AuthorizeSavedCardResponse>(url, body, { headers });
  }
  buildBrowserParameters(): AuthorizeSavedCardRequest['payment']['clientRequest']['browserParameters'] {
    return {
      javaEnabled: false,
      screenHeight: window.screen?.height ?? 826,
      screenWidth: window.screen?.width ?? 411,
      timeZoneOffset: new Date().getTimezoneOffset(),
      language: navigator.language || 'en-US',
      colorDepth: window.screen?.colorDepth ?? 24,
      deviceType: 'BROWSER',
    };
  }

  private buildHttpHeaders(auth: SavedCardAuthHeaders): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      identifier: auth.identifier,
      signedElement: auth.signedElement,
      signature: auth.signature,
      'transaction-id': auth.transactionId,
    });
  }
}
